import {
  extractFlightInfoFromFile,
  type FlightFieldsToExtract,
} from "lib/ai/flightAI";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { UseFormReturn } from "react-hook-form";
import openToast from "utils/openToast";

const useExtractFlightAi = (
  form: UseFormReturn<any>,
  setExtractedFlightInfo: (data: FlightFieldsToExtract) => void,
) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const extractFlightAi = async (file: File) => {
    setIsLoading(true);
    try {
      const response = await extractFlightInfoFromFile(file!);

      if (response.error) {
        openToast(t("flightForm.extractionError"), "error");

        return;
      }

      const responseData = response.data;
      if (!responseData)
        throw new Error("No data returned from flight extraction.");

      setExtractedFlightInfo(responseData as any);

      const hasOutbound = responseData.outbound_flight_type;

      const hasReturnFlight = responseData.return_flight_type;

      const assignField = (field: FlightFieldsToExtract) => {
        if (responseData[field] !== undefined)
          form.setValue(field, responseData[field]);
      };

      const outboundType =
        responseData.outbound_flight_type != null
          ? String(responseData.outbound_flight_type)
          : "2";

      const returnType =
        responseData.return_flight_type != null
          ? String(responseData.return_flight_type)
          : "2";

      if (hasOutbound) {
        form.setValue(
          "outbound_flight_type",
          outboundType === "1" || outboundType === "2" ? outboundType : "2",
        );
        assignField("outbound_departure_date");
        assignField("outbound_departure_time");
        assignField("outbound_arrival_date");
        assignField("outbound_arrival_time");
        assignField("outbound_duration_hours");
        assignField("outbound_duration_minutes");
        assignField("outbound_duration_time");
        assignField("outbound_cabin");
        assignField("outbound_flight_number");
        assignField("outbound_flight_pnr");
        assignField("outbound_departure_airport_code");
        assignField("outbound_departure_airport_name");
        assignField("outbound_arrival_airport_code");
        assignField("outbound_arrival_airport_name");
        assignField("outbound_order_number");
        assignField("outbound_tail_number");
        assignField("outbound_call_sign");
        assignField("outbound_operator");
        assignField("outbound_aircraft");
        assignField("outbound_departure_location");
        assignField("outbound_arrival_location");
        assignField("outbound_handling_agents");
        assignField("outbound_handling_agents_contact_number");
        assignField("outbound_handling_agents_country_code");
      }

      if (hasReturnFlight) {
        form.setValue(
          "return_flight_type",
          returnType === "1" || returnType === "2" ? returnType : "2",
        );
        assignField("return_departure_date");
        assignField("return_departure_time");
        assignField("return_arrival_date");
        assignField("return_arrival_time");
        assignField("return_duration_hours");
        assignField("return_duration_minutes");
        assignField("return_duration_time");
        assignField("return_cabin");
        assignField("return_flight_number");
        assignField("return_flight_pnr");
        assignField("return_departure_airport_code");
        assignField("return_departure_airport_name");
        assignField("return_arrival_airport_code");
        assignField("return_arrival_airport_name");
        assignField("return_order_number");
        assignField("return_tail_number");
        assignField("return_call_sign");
        assignField("return_operator");
        assignField("return_aircraft");
        assignField("return_departure_location");
        assignField("return_arrival_location");
        assignField("return_handling_agents");
        assignField("return_handling_agents_contact_number");
        assignField("return_handling_agents_country_code");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return { extractFlightAi, isLoading };
};

export default useExtractFlightAi;
